/**
 * @namespace helpers
 */

/**
 * Helper functions for FP
 *
 * @module fn
 * @memberof helpers
 * @see helpers
 */

/**
 * Returns a function that always returns the given value
 * Note that for non-primitives the value returned is a reference to the original value
 *
 * @function
 * @param  {*} val - value to wrap in a function
 * @return {Function} * -> val
 */
export const always = (val) => () => val

/**
 * Function that does nothing but return the parameter supplied to it
 *
 * @function
 * @param {*} val
 * @return {*} val
 */
export const identity = (val) => val

/**
 * Noop function, do nothing
 *
 * @function
 */
export const noop = always()

/**
 * Takes a function `f` and returns a function `g` such that if called with the same arguments
 * when `f` returns a "truthy" value, `g` returns `false` and when `f` returns a "falsy" value `g` returns `true`
 *
 * @function
 */
export const complement = (f) => (...args) => !f(...args)

/**
 * Performs right-to-left function composition, math (f ∘ g)(x) = f(g(x))
 * (f1, f2, f3, f4…) => value => f1(f2(f3(f4(value))))
 * so, `compose(a, b, c)(x)` is equal to `a(b(c(x)))`
 *
 * Simple equivalent with two functions:
 * const compose = (f1, f2) => value => f1(f2(value))
 *
 * (maybe https://github.com/michaelmitchell/babel-plugin-pipe-composition)
 *
 * @function
 * @param  {Function} fns - functions
 * @return {Function}
 */
export const compose = (...fns) => fns.reduce((f, g) => (...args) => f(g(...args)))

/**
 * Check provider
 *
 * @function
 * @return {boolean}
 */
export const isTvDip = () => {
  return window.ENV.APP_PROVIDER === 'TvDip'
}

/**
 * Check form section for validation errors
 *
 * @param form
 * @param {string} sectionName
 * @param isNewEntity
 *
 * @return {boolean}
 */
export const isSectionInvalid = (form, sectionName, isNewEntity = false) => {
  for (const errorName in form.$error) {
    const condition = form.$error[errorName]
      .find(error => error.$name.includes(sectionName) && (!isNewEntity || error.$touched || form.$submitted))

    if (condition) {
      return true
    }
  }
  return false
}
